import { useState } from 'react'
import { useCaseContext } from '@broker-crm-contexts'

import { CaseStatus, useGetCaseVerificationsQuery } from '@acre/graphql'
import { Card, CenteredLoadingSpinner } from '@acre/design-system'

import IddModal from '../../../../Shared/IddModal'
import { TasksLocation } from '../CaseOverview.helpers'
import { hasCaseVerificationCollections } from '../CaseRecommendation.helpers'
import CaseTransitions from './CaseTransitions'
import { Gate } from './CaseVerifications.fixtures'
import {
  getExceptionCollections,
  groupCollectionsToOptionalAndNonOptional,
  shouldShowPreSalesCheckTask,
  verificationComparator as comparator,
} from './CaseVerifications.helpers'
import SubmitForCaseCheckButton from './SubmitForCaseCheckButton'
import TasksCardHeader from './TasksCardHeader'
import VerificationCollectionComponent from './VerificationCollection'

type Props = {
  caseId: string
  clientIds: string[]
  caseStatus: CaseStatus
  onCompleteCase: () => any
  tasksLocation?: TasksLocation
}

type IddModalTuple = [boolean, string[] | null]

const CaseVerifications = ({ caseId, caseStatus, onCompleteCase, tasksLocation }: Props) => {
  const [initialDisclosureOpen, setInitialDisclosure] = useState<IddModalTuple>([false, null]) // IDD Reverification
  const {
    details: { documents, preference_mortgage_reason, protection },
  } = useCaseContext()

  const { data, loading, error } = useGetCaseVerificationsQuery({
    variables: { id: caseId },
    fetchPolicy: 'cache-first',
  })

  const { has_client_accepted_protection_advice } = protection || {}

  const isCaseSidebarDrawer = tasksLocation === TasksLocation.CaseSidebarDrawer

  // RD-1315: Refactored from Autogenerated components to the use of useQuery hook instead
  // the check below was previously placed within JSX, rendering an empty card if it didn't pass
  if (data && hasCaseVerificationCollections(data.case)) {
    // Safe to assert not-null with '!' because of 'hasCaseVerifications' check above
    const collections = data!.case!.details!.verifications!.verification_collections

    // We want to organise our collections such that those that require action
    // appear above any that have been completed. We also want to filter out one
    // item in particular, because Luther have inadvertently left in an "Unknown" item
    //
    const collectionsSorted = [...collections].sort(comparator)

    const showCaseCheckButton = shouldShowPreSalesCheckTask(collections, caseStatus)

    // skip gate 313 as provisional protection task needs to show up early
    const collectionsToDisplay = collectionsSorted.filter(
      (collection) =>
        (!collection.passed && collection.required_for_next_transition) ||
        (!collection.passed && collection.collection_id === Gate.GATE_3_1_3),
    )

    // Exception collections are collections which do not respect the above rules
    // so should be added using some new conditionals
    // they are collection tasks that have passed but should display some tasks nonetheless
    const exceptionCollections = getExceptionCollections(
      collectionsSorted,
      has_client_accepted_protection_advice,
      caseStatus,
      preference_mortgage_reason,
      documents,
      // Need to cast it to be of this type, as gql generated type only includes a subset of fields in verifications
    ) as typeof collections

    collectionsToDisplay.push(...exceptionCollections)

    const { optional, nonOptional } = groupCollectionsToOptionalAndNonOptional(collectionsToDisplay)

    // Max number of tasks that are shown on the case overview should be 7
    const limitedVerifications = [...nonOptional, ...optional].slice(0, 7)

    // If all verifications have passed, we will want to show the eNBS call to action
    const isCaseComplete = caseStatus === CaseStatus.Complete

    const heading =
      !isCaseComplete && collections.length > 0 ? 'caseOverview.headings.tasks' : 'caseOverview.headings.noTasks'

    const allNonOptionalPassed =
      nonOptional.length === 0 || nonOptional.every(({ collection_id }) => collection_id === Gate.GATE_3_1_3)

    const taskContent = (
      <>
        {!isCaseComplete && (
          <>
            {showCaseCheckButton && (
              <SubmitForCaseCheckButton caseId={caseId} caseStatus={caseStatus} tasksLocation={tasksLocation} />
            )}
            {/* Display case transition tasks as the first tasks in the list, if the rest of verifications are optional */}
            {allNonOptionalPassed && (
              <CaseTransitions
                caseId={caseId}
                caseStatus={caseStatus}
                onCompleteCase={onCompleteCase}
                preferenceMortgageReason={preference_mortgage_reason}
                tasksLocation={tasksLocation}
              />
            )}
            {limitedVerifications.length > 0 &&
              // If there are collections to display display them
              limitedVerifications.map((collection, index) => (
                <VerificationCollectionComponent
                  triggerInitialDisclosure={(clientIds) => setInitialDisclosure([true, clientIds])}
                  key={index}
                  collection={collection}
                  showAllVerifications={limitedVerifications.length <= 5}
                  tasksLocation={tasksLocation}
                />
              ))}
          </>
        )}
        <IddModal
          isOpen={initialDisclosureOpen[0]}
          clientIds={initialDisclosureOpen[1]!} // We can ! here because if [0] is true, this will be an array of IDs
          caseId={caseId}
          onClose={() => setInitialDisclosure([false, null])}
        />
      </>
    )

    return isCaseSidebarDrawer ? (
      taskContent
    ) : (
      <Card id="Tasks" border={false}>
        <TasksCardHeader heading={heading} />
        {taskContent}
      </Card>
    )
  }

  const loadingOrErrorContent = (
    <>
      {loading && <CenteredLoadingSpinner />}
      {error && `${error.message}`}
    </>
  )

  return isCaseSidebarDrawer ? (
    loadingOrErrorContent
  ) : (
    <Card id="Tasks" border={false}>
      {loadingOrErrorContent}
    </Card>
  )
}

export default CaseVerifications
